import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { FormikProvider, useFormik } from 'formik';
import { styled, useTheme } from '@mui/material/styles';
import { Container, Paper, Typography, Button, Grid } from '@mui/material';
import Page from 'src/components/Page';
import * as dealsApi from 'src/services/APIs/dealsApi';
import { useSelector } from 'src/redux/hooks';
import { useSignedLinkAuth } from 'src/hooks';
import { getDealSelector } from 'src/redux/selectors/isoPortal';
import { RenderFormikTextField } from 'src/utils/formikFormUtils';

const RootStyle = styled('div')(({ theme }) => ({
  backgroundColor: '#fff',
  [theme.breakpoints.up('md')]: {
    display: 'flex',
  },
}));

const ContentStyle = styled('div')(({ theme }) => ({
  margin: 'auto',
  minHeight: '100vh',
  display: 'flex',
  justifyContent: 'start',
  flexDirection: 'column',
  padding: theme.spacing(12, 0),
  paddingTop: theme.spacing(2),
}));

const validationSchema = Yup.object().shape({
  notes: Yup.string().required(),
});

export default function RejectedPage() {
  const { enqueueSnackbar } = useSnackbar();
  const { epk, et } = useSignedLinkAuth();
  const theme = useTheme();
  const deal = useSelector(getDealSelector);

  const onSubmit = async (values: { notes: string }) => {
    try {
      await dealsApi.postRequestException({
        dealId: deal!.dealId!,
        notes: values.notes,
        epk,
        et,
      });

      enqueueSnackbar('Exception was requested.');
    } catch (error) {
      enqueueSnackbar('Error requesting exception: Please try again!', { variant: 'error' });
    }
  };

  const formik = useFormik({
    initialValues: {
      notes: '',
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit,
  });

  return (
    <Page title="Libertas - Offer Rejected">
      <RootStyle>
        <Container sx={{ position: 'relative', height: '100%', pt: '68px' }}>
          <ContentStyle>
            <Paper
              sx={{
                backgroundColor: theme.palette.background.paper,
                borderRadius: '4px',
                padding: '4px',
                marginBottom: theme.spacing(2),
              }}
            >
              <FormikProvider value={formik}>
                <Typography variant="h4" gutterBottom>
                  This application is currently in{' '}
                  <Typography color="error" variant="h4" display="inline">
                    rejected
                  </Typography>{' '}
                  status
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    {RenderFormikTextField({
                      renderError: false,
                      helpers: { ...formik.getFieldProps('notes') },
                      label: 'Request Exceptions',
                      required: false,
                      autoComplete: 'off',
                      multiline: true,
                      rows: 4,
                      formik,
                    })}
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      type="submit"
                      disabled={!formik.isValid || formik.isSubmitting}
                      sx={{ borderRadius: Number(theme.shape.borderRadius) * 2 }}
                      onClick={() => formik.handleSubmit()}
                    >
                      Send
                    </Button>
                  </Grid>
                </Grid>
              </FormikProvider>
            </Paper>
          </ContentStyle>
        </Container>
      </RootStyle>
    </Page>
  );
}
